import {
  changeUserLevelService,
  createCountryLevelLimitService,
  deleteCountryLevelLimitService,
  getCountryLevelLimitsService,
  getLevelConfigsService,
  getPromotionConditionsService,
  getUserLevelsService,
  updateCountryLevelLimitService,
  updateLevelConfigService,
  updatePromotionConditionService,
} from "@/services/levels.service";
import type {
  ChangeUserLevelBody,
  CreateCountryLevelLimitBody,
  UpdateCountryLevelLimitBody,
  UpdateLevelConfigBody,
  UpdatePromotionConditionBody,
} from "@/types/levels.types";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";

const LEVELS_KEY = ["levels"] as const;

/* ── User Levels ───────────────────────────────────────────── */

interface UserLevelFilters {
  page?: number;
  limit?: number;
  level?: number;
}

export const useUserLevels = (filters: UserLevelFilters = {}) =>
  useQuery({
    queryKey: [...LEVELS_KEY, "users", filters],
    queryFn: async () => {
      const {data} = await getUserLevelsService(filters);
      if (data === undefined) throw new Error("No data");
      return data;
    },
  });

export const useChangeUserLevel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({userId, body}: {userId: string; body: ChangeUserLevelBody}) => changeUserLevelService(userId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: [...LEVELS_KEY, "users"]});
    },
  });
};

/* ── Level Configs ─────────────────────────────────────────── */

export const useLevelConfigs = () =>
  useQuery({
    queryKey: [...LEVELS_KEY, "configs"],
    queryFn: async () => {
      const {data} = await getLevelConfigsService();
      if (data === undefined) throw new Error("No data");
      return data;
    },
  });

export const useUpdateLevelConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({level, body}: {level: number; body: UpdateLevelConfigBody}) => updateLevelConfigService(level, body),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: [...LEVELS_KEY, "configs"]});
    },
  });
};

/* ── Promotion Conditions ──────────────────────────────────── */

export const usePromotionConditions = () =>
  useQuery({
    queryKey: [...LEVELS_KEY, "conditions"],
    queryFn: async () => {
      const {data} = await getPromotionConditionsService();
      if (data === undefined) throw new Error("No data");
      return data;
    },
  });

export const useUpdatePromotionCondition = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({conditionId, body}: {conditionId: string; body: UpdatePromotionConditionBody}) =>
      updatePromotionConditionService(conditionId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: [...LEVELS_KEY, "conditions"]});
    },
  });
};

/* ── Country Level Limits ──────────────────────────────────── */

export const useCountryLevelLimits = (countryId: string) =>
  useQuery({
    queryKey: [...LEVELS_KEY, "country-limits", countryId],
    queryFn: async () => {
      const {data} = await getCountryLevelLimitsService(countryId);
      if (data === undefined) throw new Error("No data");
      return data;
    },
    enabled: !!countryId,
  });

export const useCreateCountryLevelLimit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCountryLevelLimitBody) => createCountryLevelLimitService(body),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: [...LEVELS_KEY, "country-limits"]});
    },
  });
};

export const useUpdateCountryLevelLimit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({limitId, body}: {limitId: string; body: UpdateCountryLevelLimitBody}) =>
      updateCountryLevelLimitService(limitId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: [...LEVELS_KEY, "country-limits"]});
    },
  });
};

export const useDeleteCountryLevelLimit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (limitId: string) => deleteCountryLevelLimitService(limitId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: [...LEVELS_KEY, "country-limits"]});
    },
  });
};
